import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import supabase from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

// Helper function for promise timeout
const withTimeout = (promise, ms, timeoutError = new Error('Promise timed out')) => {
  const timeout = new Promise((_, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(timeoutError);
    }, ms);
  });
  return Promise.race([promise, timeout]);
};

const PROFILE_FETCH_TIMEOUT = 7000; // 7 seconds

// Create context with default values
const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,       // True when initially checking auth session
  profile: null,
  subscription: null,  // User's active/trialing subscription
  hasProAccess: false, // True if user has access to Pro features
  proAccessLoading: true, // True when checking profile/subscription for Pro access
  setLoading: () => {},
  signUp: async () => ({ error: new Error('Auth not initialized') }),
  signIn: async () => ({ error: new Error('Auth not initialized') }),
  signOut: async () => ({ error: new Error('Auth not initialized') }),
  signInWithGoogle: async () => ({ error: new Error('Auth not initialized') }),
  resetPassword: async () => ({ error: new Error('Auth not initialized') }),
  updatePassword: async () => ({ error: new Error('Auth not initialized') })
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true); // Overall auth loading
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null); // New state
  const [hasProAccess, setHasProAccess] = useState(false); // New state
  const [proAccessLoading, setProAccessLoading] = useState(true); // New state for pro access check
  const [event, setEvent] = useState(null); // New state to store the event type
  const navigate = useNavigate();

  // New function to fetch profile, subscription, and determine pro access
  const fetchProfileAndSubscriptionAndDetermineAccess = async (currentSession) => {
    console.log('[FPADAA] Called for user:', currentSession?.user?.email); // FPADAA = FetchProfileAndDetermineAccessAccess
    if (!currentSession?.user) {
      console.log('[FPADAA] No user in session, resetting profile/sub/access.');
      setProfile(null);
      setSubscription(null);
      setHasProAccess(false);
      setProAccessLoading(false); // Reset here too
      setLoading(false); // Also set main loading false if no user in session
      return;
    }

    console.log('[FPADAA] Setting proAccessLoading to true and main loading to true.');
    setProAccessLoading(true);
    setLoading(true); // Ensure main loading is active while FPADAA runs

    let userProfile = null;
    let userSubscription = null;
    let calculatedProAccess = false;

    try {
      // Granular try-catch for profile fetch
      let profileDataToUse, profileErrorToUse;
      try {
        console.log(`[FPADAA] ABOUT TO CALL supabase.from('profiles').select with ${PROFILE_FETCH_TIMEOUT}ms timeout for user ID: ${currentSession.user.id}`);
        const profilePromise = supabase
          .from('profiles')
          .select('*, is_subscribed, subscription_status, is_admin, has_lifetime_access') // Explicitly select all needed fields
          .eq('id', currentSession.user.id)
          .single();
        
        const response = await withTimeout(
          profilePromise,
          PROFILE_FETCH_TIMEOUT,
          new Error(`[FPADAA] Profile fetch timed out after ${PROFILE_FETCH_TIMEOUT}ms for user ID: ${currentSession.user.id}`)
        );

        console.log('[FPADAA] Await for profile select RESOLVED.'); // Keeping this log
        profileDataToUse = response.data;
        profileErrorToUse = response.error;
        console.log('[FPADAA] supabase.from(\'profiles\').select call COMPLETED.');
        console.log('[FPADAA] Raw profileData response:', profileDataToUse);
        console.log('[FPADAA] Raw profileError response:', profileErrorToUse);
      } catch (e) {
        if (e.message.includes('Profile fetch timed out')) {
          console.warn('[FPADAA] Profile fetch explicitly timed out:', e.message);
        } else {
          console.error('[FPADAA] CRITICAL ERROR during supabase.from(\'profiles\').select call (not a timeout):', e);
        }
        // Ensure all loading states are reset and we exit FPADAA
        setProfile(null);
        setSubscription(null);
        setHasProAccess(false);
        setProAccessLoading(false); 
        setLoading(false); // Also set main loading to false here as a critical safety net
        console.log('[FPADAA] All loading states reset due to critical profile fetch error or timeout.');
        return; // Exit FPADAA early
      }
      
      // Use profileDataToUse and profileErrorToUse from the inner try-catch
      console.log('[FPADAA] Profile fetch supabase call completed (outer context).'); // This log might be redundant now
      console.log('[FPADAA] Raw profileData (outer context):', profileDataToUse);
      console.log('[FPADAA] Raw profileError (outer context):', profileErrorToUse);

      if (profileErrorToUse && profileErrorToUse.code !== 'PGRST116') {
        console.error('[FPADAA] Error fetching profile (and not PGRST116):', profileErrorToUse.message, profileErrorToUse);
      } else if (!profileDataToUse) {
        console.log('[FPADAA] Profile not found for user, creating basic profile...');
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert([{ 
            id: currentSession.user.id,
            email: currentSession.user.email,
            full_name: currentSession.user.user_metadata?.full_name || '',
            // New profiles won't have subscription status yet, defaults in DB schema should apply
          }])
          .select('*, is_subscribed, subscription_status, is_admin, has_lifetime_access') // Match the explicit select here too
          .single();
        if (insertError) {
          console.error('[FPADAA] Error creating profile:', insertError.message);
        } else {
          profileDataToUse = newProfile;
          console.log('[FPADAA] Profile created successfully.');
        }
      }
      userProfile = profileDataToUse;
      setProfile(userProfile);
      console.log('[FPADAA] Profile fetch/create complete. Profile:', userProfile);

      // If profile fetch failed catastrophically and we returned, this part won't run.
      // If profile was just not found and created, or fetched, continue to subscription.
      if (!userProfile && (profileErrorToUse && profileErrorToUse.code !== 'PGRST116') ) {
        // This condition handles if profile fetch had an error (not PGRST116) and profile is null
        // The critical error catch above should handle most hangs/errors, but this is a safeguard
        console.warn('[FPADAA] Skipping subscription check due to profile fetch error.');
      } else {
        console.log('[FPADAA] Attempting to fetch subscription...');
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('status')
          .eq('user_id', currentSession.user.id)
          .in('status', ['active', 'trialing'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError) {
          console.error('[FPADAA] Error fetching subscription:', subError.message);
        }
        userSubscription = subData || null;
        setSubscription(userSubscription);
        console.log('[FPADAA] Subscription fetch complete. Subscription:', userSubscription);

        calculatedProAccess = !!(
          userProfile?.is_admin === true ||
          userProfile?.has_lifetime_access === true ||
          (userSubscription && ['active', 'trialing'].includes(userSubscription.status))
        );
        setHasProAccess(calculatedProAccess);
        console.log('[FPADAA] Calculated hasProAccess:', calculatedProAccess);

      }
    } catch (error) {
      console.error('[FPADAA] CATCH BLOCK reached. Error in profile/subscription fetch process:', error.message, error);
      setHasProAccess(false); // Default to no access on error
    } finally {
      console.log('[FPADAA] Finally block. Calculated Pro Access:', calculatedProAccess, 'Profile:', userProfile, 'Subscription:', userSubscription);
      setProAccessLoading(false);
      console.log('[FPADAA] proAccessLoading has been set to false.');
      setLoading(false); // Main loading state
      console.log('[FPADAA] setLoading(false) has JUST been called. Main app loading should now be false.');
      console.log('[FPADAA] End of Finally block. Calculated Pro Access:', calculatedProAccess);
    }
  }; // End of fetchProfileAndSubscriptionAndDetermineAccess

  useEffect(() => {
    const getInitialSession = async () => {
      console.log('[GIS] Getting initial session...');
      // setLoading(true); // Already true by default, or managed by specific flows

      try {
        console.log('[GIS] Calling supabase.auth.getSession()...');
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        console.log('[GIS] supabase.auth.getSession() returned. Session:', currentSession, 'Error:', error);

        if (error) {
          console.error('[GIS] Error getting session:', error.message);
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          // setLoading(false); // Defer to onAuthStateChange
          // setProAccessLoading(false); // Defer to onAuthStateChange or FPADAA completion
          return; // Exit if error
        }

        if (currentSession) {
          console.log('[GIS] Session found. User:', currentSession.user?.email);
          setUser(currentSession.user);
          setSession(currentSession);
          // setLoading(true); // Ensure loading remains true while fetching profile
          // setProAccessLoading(true); // FPADAA will handle this
          // await fetchProfileAndSubscriptionAndDetermineAccess(currentSession); // This will be triggered by onAuthStateChange
        } else {
          console.log('[GIS] No session found.');
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          // setLoading(false); // Defer to onAuthStateChange
          // setProAccessLoading(false); // Defer to onAuthStateChange
        }
      } catch (e) {
        console.error('[GIS] Critical error in getInitialSession:', e);
        setUser(null);
        setSession(null);
        setProfile(null);
        setSubscription(null);
        setHasProAccess(false);
        // setLoading(false); // Defer to onAuthStateChange
        // setProAccessLoading(false); // Defer to onAuthStateChange
      } finally {
        console.log('[GIS] getInitialSession finally block. Current loading state likely true, awaiting onAuthStateChange.');
        // setLoading(false); // IMPORTANT: Removed this. Let onAuthStateChange handle it.
      }
    };

    getInitialSession();

    // Correctly destructure to get the subscription object
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log(`[AuthListener] Event received: ${event}`);
        console.log('[AuthListener] Session received:', currentSession);
        console.log('[AuthListener] Current window.location.hash:', window.location.hash);

        setEvent(event); // Store the event type

        // Always update user and session state first
        setUser(currentSession?.user ?? null);
        setSession(currentSession);

        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthListener] PASSWORD_RECOVERY event detected.');
          // Handle password recovery event if needed
          setLoading(false); // Ensure loading stops
          setProAccessLoading(false);
        } else if (event === 'SIGNED_IN') {
          console.log('[AuthListener] SIGNED_IN event detected.');
          if (currentSession?.user) {
            console.log(`[AuthListener] SIGNED_IN: Is OAuth session? ${!!currentSession.provider_token || !!currentSession.provider_refresh_token}`);
            const isOAuth = !!currentSession.provider_token || !!currentSession.provider_refresh_token;
            if (isOAuth) {
              console.log('[AuthListener] SIGNED_IN: OAuth detected, navigation/clearing hash handled. Loading states will be reset by FPADAA if called or by other event handlers.');
              // If OAuth requires immediate profile fetch, it would be called here, and FPADAA would handle loading.
              // If not, we assume the UI is waiting for the session to settle and then potentially an INITIAL_SESSION or USER_UPDATED.
            } else {
              console.log('[AuthListener] SIGNED_IN: Non-OAuth detected. INITIAL_SESSION event will handle profile fetch if this establishes the session.');
              // REMOVED delayed fetch: INITIAL_SESSION is expected to handle this for page loads/refreshes.
              // setTimeout(async () => {
              //   console.log('[AuthListener] Delay complete, now fetching profile/sub for SIGNED_IN.');
              //   await fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
              //   console.log('[AuthListener] SIGNED_IN: fetchProfileAndSubscriptionAndDetermineAccess completed after delay.');
              // }, 250); 
            }
            // setLoading(false); // REMOVED: FPADAA handles this if it's called.
            // setProAccessLoading(false); // REMOVED: FPADAA handles this.
            // console.log('[AuthListener] SIGNED_IN: Redundant loading state reset removed.'); // Log cleanup
          } else {
            console.error('[AuthListener] SIGNED_IN event received, but session or user is null/undefined!', currentSession);
            // Clear potentially stale data if SIGNED_IN has no user
            setProfile(null);
            setSubscription(null);
            setHasProAccess(false);
            setProAccessLoading(false);
          }
        } else if (event === 'INITIAL_SESSION' && currentSession?.user) {
          console.log('[AuthListener] INITIAL_SESSION event detected.');
          setUser(currentSession.user);
          setSession(currentSession);
          setEvent('INITIAL_SESSION'); // Set the event state

          console.log('[AuthListener] INITIAL_SESSION: User found, fetching profile/sub...');
          await fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
          console.log('[AuthListener] INITIAL_SESSION: fetchProfile completed.');
          // setLoading(false); // REMOVED: FPADAA handles this.
          // setProAccessLoading(false); // REMOVED: FPADAA handles this.
          // console.log('[AuthListener] INITIAL_SESSION: Redundant loading state reset removed.'); // Log cleanup
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          console.log(`[AuthListener] Event received: ${event}`);
          // Explicitly clear all user-related state
          setUser(null);
          setSession(null);
          setProfile(null);
          setSubscription(null);
          setHasProAccess(false);
          setLoading(false);
          setProAccessLoading(false);
        } else if (event === 'USER_UPDATED' && currentSession?.user) {
          console.log('[AuthListener] USER_UPDATED: Re-fetching profile/subscription...');
          await fetchProfileAndSubscriptionAndDetermineAccess(currentSession);
          console.log('[AuthListener] USER_UPDATED: fetchProfile completed.');
          // setLoading might not be needed here unless USER_UPDATED implies a loading state
        } else {
          // Catch-all for other events or unexpected null sessions
          console.log(`[AuthListener] Unhandled event (${event}) or null session. Setting loading false.`);
          setLoading(false);
          // Decide if proAccessLoading should be false here too, likely yes
          setProAccessLoading(false);
        }
      }
    );

    return () => {
      console.log('[AuthListener] Unsubscribing from onAuthStateChange.');
      // Call unsubscribe on the correct object
      subscription?.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount


  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) return { error };
      navigate('/');
      return { error: null };
    } catch (error) { return { error }; }
    finally { setLoading(false); }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    return { data, error };
  };

  const signUp = async (email, password, fullName) => {
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    setLoading(false);
    return { data, error };
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const redirectBaseUrl = window.location.origin;
      localStorage.setItem('authRedirectUrl', redirectBaseUrl + (window.location.pathname === '/' ? '' : window.location.pathname) );
      localStorage.setItem('googleAuthInProgress', 'true');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectBaseUrl,
          scopes: 'email profile',
          queryParams: { access_type: 'offline', prompt: 'consent' }
        },
      });
      if (error) {
        localStorage.removeItem('googleAuthInProgress');
        localStorage.removeItem('authRedirectUrl');
        setLoading(false);
        return { error };
      }
      return { data };
    } catch (err) {
      localStorage.removeItem('googleAuthInProgress');
      localStorage.removeItem('authRedirectUrl');
      setLoading(false);
      return { error: err };
    }
  };

  const resetPassword = async (email) => {
    setLoading(true);
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    setLoading(false);
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);
    return { data, error };
  };

  const value = useMemo(() => ({
    user,
    session,
    loading,
    profile,
    subscription,
    hasProAccess,
    proAccessLoading,
    setLoading, // Though usually not directly used by consumers if loading states are specific
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    resetPassword,
    updatePassword
  }), [user, session, loading, profile, subscription, hasProAccess, proAccessLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
} // This is the closing brace for AuthProvider
